/**
 * Componente de mensagem citada (quoted message)
 * 
 * Renderiza a mensagem original que está sendo respondida dentro
 * do bubble da resposta. Clicável para scroll até a mensagem original.
 */

import { Image, Mic, FileText, Video, MapPin, Contact, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageAttachment } from '@/pages/tenant/Chat';
import { getStorageUrl } from '@/lib/storage';

// Interface para dados da mensagem citada (subset da Message)
interface QuotedMessage {
  id: string;
  content: string | null;
  message_type: string;
  sender_type: string;
  attachments?: MessageAttachment[];
}

interface QuotedMessageBubbleProps {
  /** Dados da mensagem citada */
  quotedMessage: QuotedMessage;
  /** Direção da mensagem atual (para estilização) */
  direction: 'inbound' | 'outbound';
  /** Callback ao clicar para scroll até mensagem original */
  onQuoteClick?: () => void;
}

/**
 * Verifica se o texto contém URL
 */
function containsUrl(text: string | null): boolean {
  if (!text) return false;
  return /https?:\/\/[^\s]+/.test(text);
}

/**
 * Trunca URLs longas para evitar overflow no layout
 */
function truncateUrls(text: string, maxLength: number = 40): string {
  return text.replace(/(https?:\/\/[^\s]+)/g, (url) => {
    if (url.length > maxLength) {
      return url.substring(0, maxLength - 3) + '...';
    }
    return url;
  });
}

/**
 * Retorna ícone e texto de preview baseado no tipo
 */
function getQuotedPreview(message: QuotedMessage): { icon: React.ReactNode; text: string; thumbnail?: string } {
  // Verifica se tem thumbnail de imagem
  const imageAttachment = message.attachments?.find(a => a.type === 'image');
  
  switch (message.message_type) {
    case 'image':
      return {
        icon: <Image className="h-3 w-3 shrink-0" />,
        text: message.content || 'Foto',
        thumbnail: imageAttachment?.url,
      };
    case 'audio':
      return {
        icon: <Mic className="h-3 w-3 shrink-0" />,
        text: 'Mensagem de voz',
      };
    case 'video':
      return {
        icon: <Video className="h-3 w-3 shrink-0" />,
        text: message.content || 'Vídeo',
      };
    case 'document':
      return {
        icon: <FileText className="h-3 w-3 shrink-0" />,
        text: message.content || 'Documento',
      };
    case 'location':
      return {
        icon: <MapPin className="h-3 w-3 shrink-0" />,
        text: message.content || 'Localização',
      };
    case 'contact':
      return {
        icon: <Contact className="h-3 w-3 shrink-0" />,
        text: message.content || 'Contato',
      };
    default:
      // Detecta se mensagem de texto contém URL para exibir ícone de link
      if (containsUrl(message.content)) {
        return {
          icon: <Link2 className="h-3 w-3 shrink-0" />,
          // Trunca URLs longas para evitar overflow no container
          text: truncateUrls(message.content || '', 50),
        };
      }
      return {
        icon: null,
        text: message.content || '',
      };
  }
}

export function QuotedMessageBubble({ 
  quotedMessage, 
  direction, 
  onQuoteClick 
}: QuotedMessageBubbleProps) {
  const preview = getQuotedPreview(quotedMessage);
  const senderLabel = quotedMessage.sender_type === 'agent' ? 'Você' : 'Contato';
  
  // Estilos baseados na direção da mensagem atual
  const isOutbound = direction === 'outbound';

  return (
    <div
      onClick={onQuoteClick}
      className={cn(
        // Container principal com overflow-hidden para impedir expansão
        'flex gap-2 p-2 mb-2 rounded cursor-pointer transition-colors overflow-hidden max-w-full',
        'border-l-2',
        !isOutbound && 'bg-background/50 border-primary/50 hover:bg-background/70'
      )}
      style={isOutbound ? {
        backgroundColor: 'hsl(var(--chat-text-primary) / 0.05)',
        borderLeftColor: 'hsl(var(--chat-text-secondary))'
      } : undefined}
    >
      {/* Conteúdo - min-w-0 permite encolher abaixo do conteúdo */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {/* Remetente - cor fixa WhatsApp para outbound */}
        <p 
          className={cn('text-[10px] font-medium truncate', !isOutbound && 'text-primary')}
          style={isOutbound ? { color: 'hsl(200 98% 36%)' } : undefined}
        >
          {senderLabel}
        </p>
        
        {/* Preview da mensagem - limita a 2 linhas com quebra */}
        <div 
          className={cn('text-xs min-w-0', !isOutbound && 'text-muted-foreground')}
          style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
        >
          <div className="flex items-start gap-1">
            {preview.icon && (
              <span className="shrink-0 mt-0.5">{preview.icon}</span>
            )}
            {/* Texto limitado a 2 linhas com quebra forçada para URLs */}
            <p className="line-clamp-2 break-all overflow-hidden min-w-0">
              {preview.text}
            </p>
          </div>
        </div>
      </div>

      {/* Thumbnail de imagem (se houver) */}
      {preview.thumbnail && (
        <div className="w-10 h-10 rounded overflow-hidden shrink-0">
          <img
            src={getStorageUrl(preview.thumbnail) || preview.thumbnail}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
}
